import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, FastForward } from 'lucide-react';

const GalaxySpinner = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const animationRef = useRef(null);
  const [speed, setSpeed] = useState(5);
  const [isPlaying, setIsPlaying] = useState(true);
  const speedRef = useRef(speed);
  const isPlayingRef = useRef(isPlaying);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    initStars();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
    if (isPlaying) {
      if (animationRef.current === null) {
        animate();
      }
    } else if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, [isPlaying]);

  const initStars = () => {
    starsRef.current = [];
    for (let i = 0; i < 800; i++) {
      starsRef.current.push({
        x: Math.random() * canvasRef.current.width - canvasRef.current.width / 2,
        y: Math.random() * canvasRef.current.height - canvasRef.current.height / 2,
        z: Math.random() * canvasRef.current.width,
        size: Math.random() * 2
      });
    }
  };

  const animate = () => {
    if (!isPlayingRef.current) {
      animationRef.current = null;
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      animationRef.current = null;
      return;
    }
    const ctx = canvas.getContext('2d');
    const speedNow = speedRef.current;

    ctx.fillStyle = 'rgba(10, 10, 30, 0.3)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    starsRef.current.forEach(star => {
      star.z -= speedNow;

      if (star.z <= 0) {
        star.z = canvas.width;
        star.x = Math.random() * canvas.width - centerX;
        star.y = Math.random() * canvas.height - centerY;
      }

      const k = 128 / star.z;
      const px = star.x * k + centerX;
      const py = star.y * k + centerY;

      if (px >= 0 && px <= canvas.width && py >= 0 && py <= canvas.height) {
        const size = (1 - star.z / canvas.width) * star.size * 3;
        const opacity = 1 - star.z / canvas.width;

        // Create star trail
        const trailLength = speedNow * 2;
        const px2 = star.x * (128 / (star.z + trailLength)) + centerX;
        const py2 = star.y * (128 / (star.z + trailLength)) + centerY;

        const gradient = ctx.createLinearGradient(px2, py2, px, py);
        gradient.addColorStop(0, `rgba(255, 255, 255, 0)`);
        gradient.addColorStop(1, `rgba(255, 255, 255, ${opacity})`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = size;
        ctx.beginPath();
        ctx.moveTo(px2, py2);
        ctx.lineTo(px, py);
        ctx.stroke();
      }
    });

    animationRef.current = requestAnimationFrame(animate);
  };

  const togglePlay = () => setIsPlaying(!isPlaying);

  return (
    <div className="min-h-screen bg-[#0a0a1e] relative overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
      />

      <div className="relative z-10 p-6">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="glass-effect text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-white">Galaxy Spinner</h1>
          
          <button
            data-testid="toggle-play"
            onClick={togglePlay}
            className="glass-effect text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isPlaying ? 'Pause' : 'Play'}
          </button>
        </header>

        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 glass-effect rounded-2xl p-6 min-w-96">
          <div className="flex items-center gap-6">
            <FastForward className="w-6 h-6 text-white" />
            <div className="flex-1">
              <label className="block text-white text-sm font-semibold mb-2">
                Warp Speed: {speed}x
              </label>
              <input
                data-testid="speed-slider"
                type="range"
                min="1"
                max="20"
                value={speed}
                onChange={(e) => setSpeed(Number(e.target.value))}
                className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>

        <div className="fixed top-32 left-8 glass-effect rounded-2xl p-4 text-white max-w-xs">
          <p className="text-sm">
            Experience the sensation of traveling through space at warp speed. Adjust the speed and feel the stars rush by!
          </p>
        </div>
      </div>
    </div>
  );
};

export default GalaxySpinner;